import { Box, Code, Link, useColorModeValue } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { citedEntryId } from '../citations';

interface GuideMarkdownProps {
  content: string;
  /** Opens a guide entry when a citation link is clicked */
  onOpenEntry: (entryId: string) => void;
  testId?: string;
  /** Body text size; headings and code scale with it */
  fontSize?: string;
}

/** Renders guide Markdown (entries and Ask answers), with tables and entry links */
export function GuideMarkdown({ content, onOpenEntry, testId, fontSize = 'md' }: GuideMarkdownProps) {
  const codeBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box
      data-testid={testId}
      fontSize={fontSize}
      sx={{
        '& h2': { fontSize: '1.125em', fontWeight: 'semibold', mt: 4, mb: 2 },
        '& h3': { fontSize: '1em', fontWeight: 'semibold', mt: 3, mb: 1 },
        '& p': { mb: 2 },
        '& ul, & ol': { pl: 5, mb: 2 },
        '& li': { mb: 1 },
        '& pre': { bg: codeBg, p: 3, borderRadius: 'md', overflowX: 'auto', mb: 2, fontSize: '0.875em' },
        '& table': { width: '100%', borderCollapse: 'collapse', mb: 3, display: 'block', overflowX: 'auto' },
        '& th, & td': { border: '1px solid', borderColor: 'border-default', px: 2, py: 1, textAlign: 'left', verticalAlign: 'top' },
        '& th': { bg: codeBg, fontWeight: 'semibold' },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code: ({ className, children }) =>
            className ? <code className={className}>{children}</code> : <Code fontSize="0.875em">{children}</Code>,
          a: ({ href, children }) => {
            const entryId = citedEntryId(href);
            if (entryId) {
              return (
                <Link
                  as="button"
                  color="brand.500"
                  fontWeight="medium"
                  data-testid={`guide-citation-${entryId}`}
                  onClick={() => onOpenEntry(entryId)}
                >
                  {children}
                </Link>
              );
            }
            return (
              <Link href={href} isExternal color="brand.500">
                {children}
              </Link>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}
